import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FaUser,
  FaEnvelope,
  FaPhone,
  FaLock,
  FaEye,
  FaEyeSlash,
  FaExclamationCircle
} from "react-icons/fa";
import styles from "./Auth.module.css";
import { apiFetch } from "../api";
import { toast } from "react-hot-toast";

export default function Signup() {
  // Form state and handlers
  const [form, setForm] = useState({
    firstName: "", lastName: "", email: "", phone: "", password: "", confirmPassword: "",
  });
  //  States
  const [errors, setErrors] = useState({});
  const [globalError, setGlobalError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isFocused, setIsFocused] = useState({ password: false, confirmPassword: false });
  const [termsAccepted, setTermsAccepted] = useState(false);
  const navigate = useNavigate();

  // Particles for background
  const particles = Array.from({ length: 20 }, (_, i) => ({
    id: i,
    duration: Math.random() * 10 + 5 + 's',
    delay: Math.random() * 5 + 's',
    left: Math.random() * 100 + '%'
  }));

  // Field-specific validation
  const validateField = (name, value) => {
    switch (name) {
      case "firstName":
        if (!value.trim()) return "Please fill the First Name";
        if (!/^[a-zA-Z\s]+$/.test(value)) return "Name only contains letters";
        break;
      case "lastName":
        if (!value.trim()) return "Please fill the Last Name";
        if (!/^[a-zA-Z\s]+$/.test(value)) return "Name only contains letters";
        break;
      case "email":
        if (!value.trim()) return "Please fill the Email";
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value))
          return "Please enter a valid email address";
        break;
      case "phone":
        if (!value.trim()) return "Please fill the Phone";
        break;
      case "password":
        if (!value) return "Please fill the Password";
        if (
          !/^(?=.*[a-zA-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{6,}$/.test(
            value
          )
        ) {
          return "Password must contain min 6 chars, at least 1 number & 1 special character";
        }
        break;
      case "confirmPassword":
        if (!value) return "Please fill the Confirm Password";
        if (value !== form.password) return "Passwords don't match";
        break;
      default:
        break;
    }
    return "";
  };

  // Validate all fields on submit
  const validateAll = () => {
    const newErrors = {};
    // Validate each field
    Object.keys(form).forEach((key) => {
      const error = validateField(key, form[key]);
      if (error) newErrors[key] = error;
    });
    // Terms acceptance check
    if (!termsAccepted) newErrors.terms = "You must accept the terms and conditions";
    return newErrors;
  };

  // Handle input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    // Checkbox handling
    if (type === 'checkbox') {
        setTermsAccepted(checked);
        if (errors.terms) setErrors({ ...errors, terms: "" });
    } else {
        setForm({ ...form, [name]: value });
        const error = validateField(name, value);
        setErrors({ ...errors, [name]: error });
    }
    if (globalError) setGlobalError("");
  };

  // Handle input focus for floating label
  const handleFocus = (field) => {
    setIsFocused({ ...isFocused, [field]: true });
  };

  // Handle input blur for floating label
  const handleBlur = (field) => {
    setIsFocused({ ...isFocused, [field]: false });
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    // Validate all fields
    const validationErrors = validateAll();
    setErrors(validationErrors);

    // If there are validation errors, show global error
    if (Object.keys(validationErrors).length > 0) {
      setGlobalError("Please fill all fields correctly");
      setIsLoading(false);
      return;
    } else {
      setGlobalError("");
    }

    setIsLoading(true);

    try {
      // Use apiFetch for registration
      const data = await apiFetch('/api/users/register', {
        method: "POST",
        body: form,
      });
      toast.success(data.message || "Registered successfully", {
        position: "top-center",
      });
      navigate("/login");

    } catch (err) {
      toast.error(err.message, { position: "top-center" });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.authContainer}>
      {/* Luxury Background Elements */}
      <div className={styles.boutiqueBackground}>
        <div className={styles.luxuryPattern}></div>
        <div className={styles.crystalShape1}></div>
        <div className={styles.crystalShape2}></div>
        <div className={styles.crystalShape3}></div>
        <div className={styles.goldAccent1}></div>
        <div className={styles.goldAccent2}></div>
        
        {/* Floating Shapes */}
        <div className={styles.floatingShapes}>
          <div className={styles.shape1}></div>
          <div className={styles.shape2}></div>
          <div className={styles.shape3}></div>
          <div className={styles.shape4}></div>
        </div>
        
        {/* Animated Particles */}
        <div className={styles.particles}>
          {particles.map(particle => (
            <div
              key={particle.id}
              className={styles.particle}
              style={{
                '--duration': particle.duration,
                '--delay': particle.delay,
                left: particle.left
              }}
            ></div>
          ))}
        </div>
      </div>

      {/* Auth Card */}
      <div className={styles.authCard}>
        <div className={styles.cardHeader}>
          <h2 className={styles.cardTitle}>Create Account</h2>
        </div>

        <div className="card-body p-3">
          {globalError && (
            <div className={`alert alert-danger ${styles.alert}`}>
              <div className={styles.alertContent}>
                <FaExclamationCircle className={styles.alertIcon} />
                {globalError}
              </div>
              <div className={styles.alertShine}></div>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* First Name & Last Name Row */}
            <div className="row">
              <div className="col-md-6">
                <div className="mb-3">
                  <label className={`form-label fw-semibold ${styles.label}`}>
                    First Name
                  </label>
                  <div className={styles.inputContainer}>
                    <div className={`input-group ${styles.inputGroup}`}>
                      <span className={`input-group-text ${styles.inputIcon}`}>
                        <FaUser />
                      </span>
                      <input
                        type="text"
                        className={`form-control ${styles.formControl}`}
                        name="firstName"
                        value={form.firstName}
                        onChange={handleChange}
                        placeholder="First Name"
                      />
                      <div className={styles.inputUnderline}></div>
                    </div>
                    {errors.firstName && (
                      <div className={styles.errorText}>{errors.firstName}</div>
                    )}
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <label className={`form-label fw-semibold ${styles.label}`}>
                    Last Name
                  </label>
                  <div className={styles.inputContainer}>
                    <div className={`input-group ${styles.inputGroup}`}>
                      <span className={`input-group-text ${styles.inputIcon}`}>
                        <FaUser />
                      </span>
                      <input
                        type="text"
                        className={`form-control ${styles.formControl}`}
                        name="lastName"
                        value={form.lastName}
                        onChange={handleChange}
                        placeholder="Last Name"
                      />
                      <div className={styles.inputUnderline}></div>
                    </div>
                    {errors.lastName && (
                      <div className={styles.errorText}>{errors.lastName}</div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="mb-3">
              <label className={`form-label fw-semibold ${styles.label}`}>
                Email
              </label>
              <div className={styles.inputContainer}>
                <div className={`input-group ${styles.inputGroup}`}>
                  <span className={`input-group-text ${styles.inputIcon}`}>
                    <FaEnvelope />
                  </span>
                  <input
                    type="email"
                    className={`form-control ${styles.formControl}`}
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="name@example.com"
                  />
                  <div className={styles.inputUnderline}></div>
                </div>
                {errors.email && (
                  <div className={styles.errorText}>{errors.email}</div>
                )}
              </div>
            </div>

            {/* Email */}
            <div className="mb-3">
              <label className={`form-label fw-semibold ${styles.label}`}>
                Phone
              </label>
              <div className={styles.inputContainer}>
                <div className={`input-group ${styles.inputGroup}`}>
                  <span className={`input-group-text ${styles.inputIcon}`}>
                    <FaPhone />
                  </span>
                  <input
                    type="tel"
                    className={`form-control ${styles.formControl}`}
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    placeholder="+91 1234567890"
                  />
                  <div className={styles.inputUnderline}></div>
                </div>
                {errors.phone && (
                  <div className={styles.errorText}>{errors.phone}</div>
                )}
              </div>
            </div>

            {/* Password */}
            <div className={`mb-4 ${styles.inputContainer}`}>
              <label className={`form-label fw-semibold ${styles.floatingLabel} ${(form.password || isFocused.password) ? styles.labelFloating : ''}`}>
                Password
              </label>
              <div className={`input-group ${styles.inputGroup}`}>
                <span className={`input-group-text ${styles.inputIcon} ${isFocused.password ? styles.inputIconFocused : ''}`}>
                  <FaLock />
                </span>
                <input
                  type={showPassword ? "text" : "password"}
                  className={`form-control ${styles.formControl}`}
                  name="password"
                  value={form.password}
                  onChange={handleChange}
                  onFocus={() => handleFocus('password')}
                  onBlur={() => handleBlur('password')}
                  placeholder=" "
                />
                <button
                  className={`input-group-text ${styles.inputIcon} ${isFocused.password ? styles.inputIconFocused : ''}`}
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
                <div className={styles.inputUnderline}></div>
              </div>
              {errors.password && <div className={`text-danger ${styles.errorText}`}>{errors.password}</div>}
            </div>

            {/* Confirm Password */}
            <div className={`mb-4 ${styles.inputContainer}`}>
              <label className={`form-label fw-semibold ${styles.floatingLabel} ${(form.confirmPassword || isFocused.confirmPassword) ? styles.labelFloating : ''}`}>
                Confirm Password
              </label>
              <div className={`input-group ${styles.inputGroup}`}>
                <span className={`input-group-text ${styles.inputIcon} ${isFocused.confirmPassword ? styles.inputIconFocused : ''}`}>
                  <FaLock />
                </span>
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  className={`form-control ${styles.formControl}`}
                  name="confirmPassword"
                  value={form.confirmPassword}
                  onChange={handleChange}
                  onFocus={() => handleFocus('confirmPassword')}
                  onBlur={() => handleBlur('confirmPassword')}
                  placeholder=" "
                />
                <button
                  className={`input-group-text ${styles.inputIcon} ${isFocused.confirmPassword ? styles.inputIconFocused : ''}`}
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
                <div className={styles.inputUnderline}></div>
              </div>
              {errors.confirmPassword && <div className={`text-danger ${styles.errorText}`}>{errors.confirmPassword}</div>}
            </div>

            {/* Terms and Conditions Checkbox */}
            <div className={styles.termsContainer}>
              <input
                type="checkbox"
                id="signupTermsCheck"
                name="terms"
                checked={termsAccepted}
                onChange={handleChange}
                className={styles.termsCheckbox}
              />
              <label htmlFor="signupTermsCheck" className={styles.termsLabel}>
                I agree to the <Link to="/terms-and-conditions" target="_blank" rel="noopener noreferrer">Terms & Conditions</Link> and <Link to="/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</Link>.
              </label>
            </div>
            {errors.terms && <div className={`text-danger ${styles.errorText} mt-1`}>{errors.terms}</div>}


            {/* Submit Button */}
            <div className="d-grid">
              <button 
                type="submit" 
                className={`btn ${styles.btn} ${isLoading ? styles.btnLoading : ''}`}
                disabled={isLoading}
              >
                {isLoading && <span className={styles.spinner}></span>}
                <span className={`${styles.btnText} text-light`}>
                  {isLoading ? 'Creating Account...' : 'Create Account'}
                </span>
                <div className={styles.btnShine}></div>
              </button>
            </div>
          </form>

          {/* Footer Links */}
          <div className={`text-center mt-4 ${styles.signupText}`}>
            Already have an account?{" "}
            <Link to="/login" className={styles.authLink}>
              Sign In
              <span className={styles.linkUnderline}></span>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}